import { motion } from 'motion/react'
import React from 'react'
import { cn } from 'src/shared/components/ui/utils'

export const FeatureCard = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'p-3 sm:p-4 md:p-6 lg:p-8 relative overflow-hidden bg-white',
        className
      )}
    >
      {children}
    </div>
  )
}

export const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p className="max-w-5xl mx-auto text-left tracking-tight text-black dark:text-white text-lg sm:text-xl md:text-2xl md:leading-snug">
      {children}
    </p>
  )
}

export const FeatureDescription = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  return (
    <p
      className={cn(
        'text-sm md:text-base text-left mx-auto',
        'text-neutral-500 font-normal dark:text-neutral-300',
        'text-left mx-0 my-2'
      )}
    >
      {children}
    </p>
  )
}

export const ShoppingExperience = () => {
  return (
    <div className="relative flex py-4 sm:py-6 px-2 gap-6 sm:gap-8 md:gap-10 h-full">
      <div className="w-full p-3 sm:p-4 md:p-5 mx-auto bg-white dark:bg-neutral-900 group h-full">
        <div className="flex flex-1 w-full h-full flex-col space-y-2">
          <img
            src="/about/about-team-workspace.webp?v=white"
            alt="Merch studio workspace with samples and mockups"
            height={300}
            className="w-full object-cover object-left-top rounded-sm"
          />
        </div>
      </div>

      <div className="absolute bottom-0 z-40 inset-x-0 h-32 sm:h-48 md:h-60 bg-gradient-to-t from-white dark:from-black via-white dark:via-black to-transparent w-full pointer-events-none" />
      <div className="absolute top-0 z-40 inset-x-0 h-32 sm:h-48 md:h-60 bg-gradient-to-b from-white dark:from-black via-transparent to-transparent w-full pointer-events-none" />
    </div>
  )
}

export const QualityShowcase = () => {
  const images1 = [
    '/about/about-quality-hoodie.webp?v=white',
    '/about/about-quality-tee.webp?v=white',
    '/about/about-quality-tote.webp?v=white',
  ]

  const images2 = [
    '/about/about-quality-cap.webp?v=white',
    '/about/about-quality-jacket.webp?v=white',
  ]

  const imageVariants = {
    whileHover: {
      scale: 1.1,
      rotate: 0,
      zIndex: 100,
    },
    whileTap: {
      scale: 1.1,
      rotate: 0,
      zIndex: 100,
    },
  }

  return (
    <div className="relative flex flex-col items-start p-4 sm:p-6 md:p-8 gap-6 sm:gap-8 md:gap-10 h-full overflow-hidden">
      <div className="flex flex-row -ml-8 sm:-ml-12 md:-ml-20">
        {images1.map((image, idx) => (
          <motion.div
            variants={imageVariants}
            key={'images-first' + idx}
            style={{
              rotate: Math.random() * 20 - 10,
            }}
            whileHover="whileHover"
            whileTap="whileTap"
            className="rounded-xl -mr-2 sm:-mr-3 md:-mr-4 mt-2 sm:mt-3 md:mt-4 p-1 bg-white dark:bg-neutral-800 dark:border-neutral-700 border border-neutral-100 shrink-0 overflow-hidden"
          >
            <img
              src={image}
              alt="Premium merch product"
              width="500"
              height="500"
              className="rounded-lg h-16 w-16 sm:h-20 sm:w-20 md:h-32 md:w-32 lg:h-40 lg:w-40 object-cover shrink-0"
            />
          </motion.div>
        ))}
      </div>

      <div className="flex flex-row">
        {images2.map((image, idx) => (
          <motion.div
            key={'images-second' + idx}
            style={{
              rotate: Math.random() * 20 - 10,
            }}
            variants={imageVariants}
            whileHover="whileHover"
            whileTap="whileTap"
            className="rounded-xl -mr-2 sm:-mr-3 md:-mr-4 mt-2 sm:mt-3 md:mt-4 p-1 bg-white dark:bg-neutral-800 dark:border-neutral-700 border border-neutral-100 shrink-0 overflow-hidden"
          >
            <img
              src={image}
              alt="Premium merch product"
              width="500"
              height="500"
              className="rounded-lg h-16 w-16 sm:h-20 sm:w-20 md:h-32 md:w-32 lg:h-40 lg:w-40 object-cover shrink-0"
            />
          </motion.div>
        ))}
      </div>

      <div className="absolute left-0 z-[100] inset-y-0 w-8 sm:w-12 md:w-20 bg-gradient-to-r from-white dark:from-black to-transparent h-full pointer-events-none" />
      <div className="absolute right-0 z-[100] inset-y-0 w-8 sm:w-12 md:w-20 bg-gradient-to-l from-white dark:from-black  to-transparent h-full pointer-events-none" />
    </div>
  )
}

export const FairLabor = () => {
  return (
    <div className="relative flex gap-10  h-full group/image">
      <div className="w-full  mx-auto bg-transparent dark:bg-transparent group h-full">
        <div className="flex my-10 flex-1 w-full h-full flex-col space-y-2  relative">
          {/* TODO */}
          <img
            src="/about/about-fair-labor.webp?v=white"
            alt="Sewing machine and fabric in a bright workshop"
            height={100}
            className="h-full w-full object-cover object-center rounded-sm blur-none   transition-all duration-200"
          />
        </div>
      </div>
    </div>
  )
}

export const ClimateAware = () => {
  return (
    <div className="relative flex gap-10  h-full group/image">
      <div className="w-full  mx-auto bg-transparent dark:bg-transparent group h-full">
        <div className="flex my-10 flex-1 w-full h-full flex-col space-y-2  relative">
          {/* TODO */}
          <img
            src="/about/about-climate.webp?v=white"
            alt="Misty Nordic forest and lake"
            height={100}
            className="h-full w-full object-cover object-center rounded-sm blur-none   transition-all duration-200"
          />
        </div>
      </div>
    </div>
  )
}

// Note: This features array will be moved to a component that can use the t() function
export const getFeatures = (t: (en: string, sv: string) => string) => [
  {
    title: t(
      'One team, from idea to delivery',
      'Ett team, från idé till leverans'
    ),
    description: t(
      'Each order is a conversation with the Klayder team. Brief, products, mockups and status stay in one place, and a person at Klayder reviews before you approve.',
      'Varje order är en konversation med Klayder-teamet. Brief, produkter, mockups och status stannar på ett ställe, och en person på Klayder granskar innan ni godkänner.'
    ),
    skeleton: <ShoppingExperience />,
    className:
      'col-span-1 lg:col-span-4 border-b lg:border-r dark:border-neutral-800',
  },
  {
    title: t('Quality over quantity', 'Kvalitet framför kvantitet'),
    description: t(
      'All our suppliers are carefully selected and we have a close relationship with them.',
      'Alla våra leverantörer är noggrant utvalda och vi har ett nära samarbete med dem. Leta efter "Klayder rekommenderar" ikonen för att se produkter vi testat och rekommenderar.'
    ),
    skeleton: <QualityShowcase />,
    className: 'border-b col-span-1 lg:col-span-2 dark:border-neutral-800',
  },
  {
    title: t('Fair labor practices', 'Rättvisa arbetsförhållanden'),
    description: t(
      'We are committed to fair labor practices, all our manufacturers follow the strictest regulations and hold certificates from Fair Labor Association (FLA) and Sedex.',
      'Vi är engagerade i rättvisa arbetsförhållanden, alla våra tillverkare följer dom striktaste reglerna och innehar certifikat från Fair Labor Association (FLA) och Sedex.'
    ),
    skeleton: <FairLabor />,
    className: 'col-span-1 lg:col-span-3 lg:border-r  dark:border-neutral-800',
  },
  {
    title: t('We are climate aware', 'Vi är klimatmedvetna'),
    description: t(
      'We are climate aware and we are committed to reducing our carbon footprint. We are also committed to using recycled materials in our products.',
      'Vi är klimatmedvetna och vi är engagerade i att minska vår kolavtryck. Vi är också engagerade i att använda återvunnet material i våra produkter.'
    ),
    skeleton: <ClimateAware />,
    className: 'col-span-1 lg:col-span-3 border-b lg:border-none',
  },
]
